/**
 * Typed event bus. Replaces ad-hoc `window.dispatchEvent(new CustomEvent(...))`
 * patterns across the frontend with a type-checked publish/subscribe channel.
 *
 * Events are a discriminated union keyed on `type`. Subscribing to a specific
 * `type` narrows the payload automatically.
 *
 * See frontend/CLAUDE.md §4 and the refactor plan (Phase 7 commit 66) for the
 * migration path.
 */

type AnyEvent = { type: string };

type Listener<E> = (event: E) => void;

export type EventBus<E extends AnyEvent> = {
  publish: (event: E) => void;
  subscribe: <T extends E["type"]>(
    type: T,
    listener: Listener<Extract<E, { type: T }>>,
  ) => () => void;
};

export function createEventBus<E extends AnyEvent>(): EventBus<E> {
  const listeners = new Map<string, Set<Listener<AnyEvent>>>();

  return {
    publish(event) {
      const set = listeners.get(event.type);
      if (!set) return;
      for (const fn of set) fn(event);
    },
    subscribe(type, listener) {
      let set = listeners.get(type);
      if (!set) {
        set = new Set();
        listeners.set(type, set);
      }
      set.add(listener as Listener<AnyEvent>);
      return () => {
        const s = listeners.get(type);
        if (!s) return;
        s.delete(listener as Listener<AnyEvent>);
        if (s.size === 0) listeners.delete(type);
      };
    },
  };
}
